/**
 * Global ALM Parameters — types mirror cx-alm AlmParametersDto.
 *
 * Source of truth (server-side):
 *   services/cx-alm/.../presentation/dto/AlmParametersDto.java
 *
 * V117 / Phase 2.0.PARAMS.LCR: added LCR and NSFR calibration fields.
 * V118 / Phase 2.0.PARAMS.v2:  added EVE shock, Duration shock,
 *                               and LCR wholesale run-off rate fields.
 * V126 / Phase 3.4:            added IRRBB alert threshold fields.
 */

// ── Bucket configuration (Phase 3.5 / V127) ─────────────────────────────────

/**
 * A single time-bucket row.  daysMax = 2147483647 means open-ended (last bucket).
 */
export interface TenantBucket {
  index:      number;
  label:      string;
  daysMin:    number;
  daysMax:    number;
  /** True when the server returned the Basel defaults (no custom grid saved). */
  isDefault?: boolean;
}

export interface BucketConfig {
  buckets: TenantBucket[];
}

/** The 7 Basel III standard bands — shown as UI defaults before first save. */
export const BASEL_DEFAULTS: TenantBucket[] = [
  { index: 0, label: '0-1M',  daysMin: 0,    daysMax: 30,           isDefault: true },
  { index: 1, label: '1-3M',  daysMin: 31,   daysMax: 90,           isDefault: true },
  { index: 2, label: '3-6M',  daysMin: 91,   daysMax: 180,          isDefault: true },
  { index: 3, label: '6-12M', daysMin: 181,  daysMax: 365,          isDefault: true },
  { index: 4, label: '1-2Y',  daysMin: 366,  daysMax: 730,          isDefault: true },
  { index: 5, label: '2-5Y',  daysMin: 731,  daysMax: 1825,         isDefault: true },
  { index: 6, label: '5Y+',   daysMin: 1826, daysMax: 2147483647,   isDefault: true },
];

// ── Engine parameters ────────────────────────────────────────────────────────

export type RateColumn    = 'ZERO' | 'QUOTED';
export type Interpolation = 'LINEAR' | 'CUBIC_SPLINE' | 'CLOSEST';
export type CurveSource   = 'CX_MARKET' | 'UPLOAD';

export interface CurveAssignment {
  currency: string;   // 3-letter ISO
  curveId:  string;
}

export interface AlmParameters {
  tenantId:                       string;

  // ── Curve / rate engine ──────────────────────────────────────────────────
  rateColumn:                     RateColumn;
  interpolation:                  Interpolation;
  curveSource:                    CurveSource;

  // ── NIM placeholder rates ────────────────────────────────────────────────
  nimBaselineAssetRate:           string;       // BigDecimal as string
  nimBaselineLiabilityRate:       string;
  nimDefaultShockBp:              number;
  requireTwoEyesCurveValidation:  boolean;

  // ── LCR parameters (V117) ────────────────────────────────────────────────
  /** Minimum LCR ratio for compliance. Basel III default: 1.00 (100%). */
  lcrMinThreshold:                string;
  /** Stress horizon in days. Basel III default: 30. */
  lcrStressHorizonDays:           number;
  /** Haircut on Level 2A HQLA assets. Basel III: 0.15 (15%). */
  lcrHqlaLevel2aHaircut:          string;
  /** Haircut on Level 2B HQLA assets. Basel III: 0.50 (50%). */
  lcrHqlaLevel2bHaircut:          string;
  /** Level 2A concentration cap as fraction of total HQLA. Basel III: 0.40. */
  lcrLevel2aCapPct:               string;
  /** Level 2B concentration cap as fraction of total HQLA. Basel III: 0.15. */
  lcrLevel2bCapPct:               string;
  /** Run-off rate for stable retail deposits. Basel III: 0.05 (5%). */
  lcrRunoffRetailStable:          string;
  /** Run-off rate for less-stable retail deposits. Basel III: 0.10 (10%). */
  lcrRunoffRetailLessStable:      string;
  /** Run-off rate for operational wholesale deposits. Basel III §94: 0.25. V118. */
  lcrRunoffWholesaleOperational:  string;
  /** Run-off rate for non-operational wholesale deposits. Basel III §96: 1.00. V118. */
  lcrRunoffWholesaleNonOperational: string;

  // ── NSFR parameters (V117) ───────────────────────────────────────────────
  /** Minimum NSFR ratio for compliance. Basel III default: 1.00 (100%). */
  nsfrMinThreshold:               string;

  // ── EVE scenario shock calibration (V118) ───────────────────────────────
  /** Parallel shock magnitude in bp. BCBS 368: 200bp. */
  eveParallelShockBp:             number;
  /** Short-rate shock at short end in bp. BCBS 368: 250bp. */
  eveShortRateShockBp:            number;
  /** Steepener/flattener half-swing in bp. BCBS 368: 75bp each side. */
  eveRotationShockBp:             number;
  /** Decay parameter λ for short-rate scenarios. BCBS 368: ln(2)/4 ≈ 0.1733. */
  eveLambda:                      string;
  /** Max tenor for linear ramp normalisation in years. BCBS 368: 30. */
  eveMaxTenorYears:               number;

  // ── Duration default shock (V118) ────────────────────────────────────────
  /** Default parallel shock for Duration engine when no scenario is supplied. Default: 200bp. */
  durationDefaultShockBp:         number;

  // ── FTP v2 client rates per tenor bucket (V122) ──────────────────────────
  /** Client rate for 0–1M tenor bucket (BigDecimal as string, e.g. "0.060000"). */
  ftpClientRate0to1M:             string;
  ftpClientRate1to3M:             string;
  ftpClientRate3to6M:             string;
  ftpClientRate6to12M:            string;
  ftpClientRate1to2Y:             string;
  ftpClientRate2to5Y:             string;
  ftpClientRate5yPlus:            string;

  // ── IRRBB alert thresholds (V126 / Phase 3.4) ────────────────────────────
  /** BCBS 368 §99 EVE outlier threshold (fraction). Default 0.15 (15%). */
  eveOutlierThresholdPct:         string;
  /** NIM stress-drop alert threshold (fraction). Default 0.20 (20%). */
  nimDropAlertThresholdPct:       string;

  // ── Per-currency curve assignments ───────────────────────────────────────
  curveAssignments:               CurveAssignment[];
  updatedAt?:                     string;
  updatedBy?:                     string;
}
